import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  Typography,
  Container
} from '@mui/material';
import {
  Description as DocsIcon,
  TableChart as DataIcon,
  Edit as DraftIcon,
  CloudDownload as DownloadIcon
} from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import ChatDrawer from './ChatDrawer';
import TemplateLibraryGrid from './TemplateLibraryGrid';
import CSVDataEntry from './CSVDataEntry';
import OperatingAgreementForm from './OperatingAgreementForm';
import AIDrafting from './AIDrafting';
import DocumentDownload from './DocumentDownload';
import BriefImportPage from './BriefImportPage';
import { mapOperatingAgreementImport } from '../services/operatingAgreementImportMapper';

// True when the user selected an Operating Agreement template/package. The OA
// family has a domain disjoint from the trust/will fiduciary model and needs
// the dedicated OperatingAgreementForm rather than KeyPeopleManager + trust
// gateway questions.
function isOperatingAgreementSelection(templates) {
  return templates.some(t => {
    const name = (t.name || '').toLowerCase();
    const category = (t.category || '').toLowerCase();
    const variant = (t.variant || '').toLowerCase();
    const packages = Array.isArray(t.packages) ? t.packages.join(' ').toLowerCase() : '';
    return (
      category === 'business' ||
      variant === 'business' ||
      variant === 'flp' ||
      name === 'operating agreement' ||
      name === 'operating agreement summary' ||
      name === 'schedule c' ||
      packages.includes('operating agreement package')
    );
  });
}

function isOperatingAgreementFlpSelection(templates) {
  return templates.some(t => {
    const variant = (t.variant || '').toLowerCase();
    const packages = Array.isArray(t.packages) ? t.packages.join(' ').toLowerCase() : '';
    return variant === 'flp' || packages.includes('flp');
  });
}

/* ──────────────────────────── Step defs ───────────────────────────── */
const steps = [
  { label: 'Select Documents', description: 'Choose which estate planning documents you need', icon: <DocsIcon /> },
  { label: 'Fill Client Data',  description: 'Upload or paste client information via CSV',          icon: <DataIcon /> },
  { label: 'AI Drafting',       description: 'Customize documents with AI assistance',              icon: <DraftIcon /> },
  { label: 'Download',          description: 'Review and download your completed documents',        icon: <DownloadIcon /> }
];

/* ────────────────────────── Component ─────────────────────────────── */
export default function StepperWorkflow() {
  const [activeStep, setActiveStep]           = useState(0);
  const [selectedTemplates, setSelected]      = useState([]);
  const [clientData, setClientData]           = useState(null);
  const [draftedDocs, setDraftedDocs]         = useState([]);
  const [chatMessages, setChat]               = useState([]);
  const [selectedContext, setSelectedContext] = useState(null);
  const [sessionId, setSessionId]             = useState(null);
  const [importMode, setImportMode]           = useState(false);
  const [renderVars, setRenderVars]           = useState(null);

  /* ---------- navigation helpers ---------- */
  const next  = () => {
    // When moving from step 0 (document selection) to step 1, clear any old session
    // This ensures a fresh session for new document generation
    if (activeStep === 0 && sessionId) {
      console.log('[StepperWorkflow] Starting new document selection, clearing old session');
      setSessionId(null);
      setDraftedDocs([]);
    }
    setActiveStep(prev => prev + 1);
  };

  const back  = () => {
    // When going back to step 0, clear session so user can start fresh
    if (activeStep === 1) {
      console.log('[StepperWorkflow] Returning to document selection, clearing session for fresh start');
      setSessionId(null);
      setDraftedDocs([]);
    }
    setActiveStep(prev => prev - 1);
  };

  const reset = () => {
    setActiveStep(0);
    setSelected([]);
    setClientData(null);
    setDraftedDocs([]);
    setChat([]);
    setSelectedContext(null);
    setSessionId(null);
    setImportMode(false);
    setRenderVars(null);
  };

  /* ---------- template toggle logic ---------- */
  const toggleTemplate = template =>
    setSelected(prev =>
      prev.some(t => t.id === template.id)
        ? prev.filter(t => t.id !== template.id)
        : [...prev, template]
    );

  /* ---------- render step content ---------- */
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        if (importMode) {
          return (
            <BriefImportPage
              onGenerationComplete={(docs, sid, vars) => {
                setDraftedDocs(docs);
                if (sid) setSessionId(sid);
                if (vars) setRenderVars(vars);
                setActiveStep(3); // Skip to download
              }}
              onBack={() => setImportMode(false)}
            />
          );
        }
        return (
          <>
            <Typography variant="h4" gutterBottom>Select Documents</Typography>
            <Typography variant="body1" color="text.secondary">
              Choose one or more estate planning documents to create for your client
            </Typography>

            <Box sx={{ mt: 2, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setImportMode(true)}
              >
                Import Plan JSON
              </Button>
            </Box>

            <Box sx={{ mt: 1 }}>
              <TemplateLibraryGrid
                selectedTemplates={selectedTemplates}
                onTemplateToggle={toggleTemplate}
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                onClick={next}
                disabled={selectedTemplates.length === 0}
              >
                Next: Enter Client Data&nbsp;({selectedTemplates.length} selected)
              </Button>
            </Box>
          </>
        );

      case 1:
        if (isOperatingAgreementSelection(selectedTemplates)) {
          const initialPackage = isOperatingAgreementFlpSelection(selectedTemplates)
            ? 'Operating Agreement Package - FLP'
            : 'Operating Agreement Package - Business';
          return (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={back}>← Back to document selection</Button>
              </Box>
              <OperatingAgreementForm
                initialPackage={initialPackage}
                onSubmit={intake => {
                  const result = mapOperatingAgreementImport(intake);
                  if (result.errors.length > 0) {
                    alert('Operating Agreement intake has errors:\n\n' + result.errors.join('\n'));
                    return;
                  }
                  setClientData(result.variables);
                  next();
                }}
              />
            </Box>
          );
        }
        return (
          <CSVDataEntry
            selectedTemplates={selectedTemplates}
            onDataComplete={data => {
              setClientData(data);
              next();
            }}
            onBack={back}
          />
        );

      case 2:
        return (
          <AIDrafting
            selectedTemplates={selectedTemplates}
            clientData={clientData}
            onDraftingComplete={(docs, sid) => {
              setDraftedDocs(docs);
              // Store the session ID for potential back navigation
              if (sid) setSessionId(sid);
              next();
            }}
            onBack={back}
            existingSessionId={sessionId}
            existingDocuments={draftedDocs}
          />
        );

      case 3:
        return (
          <DocumentDownload
            documents={draftedDocs}
            sessionId={sessionId}
            renderVars={renderVars}
            onBack={() => {
              // When going back to editing, pass the existing session and documents
              setActiveStep(2);
            }}
            onStartNewClient={reset}
          />
        );

      default:
        return null;
    }
  };

  /* ---------- stepper UI ---------- */
  const renderStepper = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Document Creation Steps</Typography>
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((s, i) => (
          <Step key={s.label}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    bgcolor: activeStep >= i ? 'primary.main' : 'grey.300',
                    color: 'white', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.2rem', fontWeight: 'bold'
                  }}
                >
                  {i + 1}
                </Box>
              )}
            >
              <Typography variant="subtitle1" fontWeight="medium">{s.label}</Typography>
              <Typography variant="caption" color="text.secondary">{s.description}</Typography>
            </StepLabel>
            <StepContent />
          </Step>
        ))}
      </Stepper>
    </Box>
  );

  /* ---------- workspace container ---------- */
  const renderWorkspace = () => (
    activeStep === 2
      ? <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>{renderStepContent()}</Box>
      : (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Paper elevation={1} sx={{ p: 4 }}>
            {activeStep < steps.length
              ? renderStepContent()
              : (
                <Box>
                  <Typography variant="h5" gutterBottom>All steps completed!</Typography>
                  <Button onClick={reset} variant="contained" sx={{ mt: 2 }}>
                    Start New Client
                  </Button>
                </Box>
              )}
          </Paper>
        </Container>
      )
  );

  /* ---------- render layout ---------- */
  return (
    <MainLayout
      stepperContent={renderStepper()}
      workspaceContent={renderWorkspace()}
      chatDrawerContent={
        <ChatDrawer
          messages={chatMessages}
          onSendMessage={msg => setChat(prev => [...prev, { text: msg, sender: 'user' }])}
          selectedContext={selectedContext}
        />
      }
      chatDrawerOpen={false}
      noWorkspaceOverflow={activeStep === 2}
    />
  );
}